import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Check, Crosshair, MessageSquare, X } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { useAppConfig } from "../lib/appConfig";
import { pickElement, type ElementContext } from "../lib/bugPicker";
import { markFeatureTried } from "../lib/settings";

// Shared feedback capability. The form was first authored inline in JourneyPanel ("Your journey");
// it now lives here so the Journey panel, the App page, and the Design System page all use one
// implementation. Records persist locally (demo) under the same key Srinath's mock used.

const FEEDBACK_KEY = "ir.journey.feedback.v1";

export interface FeedbackRecord {
  type: "general" | "bug";
  msg: string;
  contact: string;
  notify: boolean;
  /** Structured element context from the point-and-click picker (bug reports only). */
  context?: ElementContext;
  ts: number;
}

export function loadFeedback(): FeedbackRecord[] {
  try {
    return JSON.parse(localStorage.getItem(FEEDBACK_KEY) ?? "[]") as FeedbackRecord[];
  } catch {
    return [];
  }
}

function saveFeedback(rec: FeedbackRecord) {
  const all = loadFeedback();
  all.push(rec);
  try {
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(all));
  } catch {
    // ignore — storage may be unavailable
  }
}

// Shared ApiStack base (same endpoint as the content overlay). When set, feedback POSTs to the
// backend (stored + emailed); otherwise it stays in localStorage only.
const FEEDBACK_API = (import.meta.env.VITE_CONTENT_API_URL ?? "").replace(/\/$/, "");
export const feedbackBackendLive = Boolean(FEEDBACK_API);

async function postFeedback(rec: FeedbackRecord): Promise<void> {
  if (!FEEDBACK_API) return;
  try {
    await fetch(`${FEEDBACK_API}/feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        type: rec.type,
        msg: rec.msg,
        contact: rec.contact,
        notify: rec.notify,
        ...(rec.context ? { context: rec.context } : {}),
      }),
    });
  } catch {
    // best-effort — the localStorage copy remains
  }
}

/** Open the global feedback dialog from anywhere (mirrors openAsk()). */
export function openFeedback(kind: "general" | "bug" = "general") {
  window.dispatchEvent(new CustomEvent("open-feedback", { detail: { kind } }));
}

export function FeedbackForm({
  onDone,
  initialType = "general",
  onPickingChange,
}: {
  onDone: () => void;
  initialType?: "general" | "bug";
  /** The modal host hides itself while the element picker is active. */
  onPickingChange?: (picking: boolean) => void;
}) {
  const [type, setType] = useState<"general" | "bug">(initialType);
  const cfg = useAppConfig();
  const { isAdmin } = useAuth();
  // Admin always has the picker; the `feedback.bugPickerPublic` overlay toggle opens it to everyone.
  const canPick = isAdmin || Boolean(cfg.feedback?.bugPickerPublic);
  const [ctx, setCtx] = useState<ElementContext | null>(null);
  // Courtesy dedupe: this visitor already reported this element (local records only — the server
  // folds duplicates via fingerprint regardless). Soft hint, never a block.
  const norm = (s: string) => s.replace(/:nth-of-type\(\d+\)/g, "");
  const dup =
    ctx !== null &&
    loadFeedback().some(
      (r) =>
        typeof r.context?.selector === "string" &&
        typeof r.context?.route === "string" &&
        norm(r.context.selector) === norm(ctx.selector) &&
        r.context.route.split("?")[0] === ctx.route.split("?")[0],
    );
  const [msg, setMsg] = useState("");
  const [contact, setContact] = useState("");
  const [notify, setNotify] = useState(false);
  const [sent, setSent] = useState<FeedbackRecord | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const hasContact = contact.trim().length > 0;
    const rec: FeedbackRecord = {
      type,
      msg: msg.trim(),
      contact: contact.trim(),
      notify: hasContact && notify,
      ...(type === "bug" && ctx ? { context: ctx } : {}),
      ts: Date.now(),
    };
    saveFeedback(rec);
    void postFeedback(rec);
    setSent(rec);
  };

  if (sent) {
    return (
      <div className="jfb-thanks">
        <span className="jfb-check">
          <Check size={24} />
        </span>
        <h4 className="jfb-thanks-h">Thank you!</h4>
        <p className="jfb-thanks-p">
          Your feedback has been recorded{sent.notify ? " — we'll notify you of updates" : ""}.
        </p>
        <div className="jfb-summary">
          <div className="jfb-summary-row">
            <span className="mono">Type</span>
            <b>{sent.type === "bug" ? "Bug" : "General"}</b>
          </div>
          <div className="jfb-summary-msg">“{sent.msg}”</div>
          {sent.contact && (
            <div className="jfb-summary-row">
              <span className="mono">Contact</span>
              <b>{sent.contact}</b>
            </div>
          )}
          {sent.context && (
            <div className="jfb-summary-row">
              <span className="mono">Element</span>
              <b>{sent.context.component ?? sent.context.selector}</b>
            </div>
          )}
          {sent.notify && (
            <div className="jfb-summary-row">
              <span className="mono">Updates</span>
              <b>Subscribed</b>
            </div>
          )}
        </div>
        <div className="jfb-actions">
          <button type="button" className="btn btn-sm" onClick={onDone}>
            Done
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => {
              setSent(null);
              setMsg("");
              setContact("");
              setNotify(false);
              setCtx(null);
              setType("general");
            }}
          >
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="jfb-form" onSubmit={submit}>
      <div className="jfb-field">
        <label className="jfb-label">Type of feedback</label>
        <div className="jfb-seg" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={type === "general"}
            className={type === "general" ? "active" : ""}
            onClick={() => setType("general")}
          >
            <MessageSquare size={14} /> General
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={type === "bug"}
            className={type === "bug" ? "active" : ""}
            onClick={() => setType("bug")}
          >
            <AlertTriangle size={14} /> Bug
          </button>
        </div>
      </div>
      {type === "bug" &&
        canPick &&
        (ctx ? (
          <div className="jfb-ctx mono">
            <Crosshair size={12} />
            <span className="jfb-ctx-name">{ctx.component ?? ctx.selector}</span>
            <button type="button" aria-label="Remove element context" onClick={() => setCtx(null)}>
              <X size={12} />
            </button>
            {dup && <span className="jfb-dup">You've reported this element before — new detail helps.</span>}
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-sm jfb-pick"
            onClick={() => {
              onPickingChange?.(true);
              void pickElement().then((picked) => {
                onPickingChange?.(false);
                if (picked) setCtx(picked);
              });
            }}
          >
            <Crosshair size={14} /> Point at it
          </button>
        ))}
      <div className="jfb-field">
        <label className="jfb-label" htmlFor="jfb-msg">
          {type === "bug" ? "What went wrong?" : "Your feedback"}
        </label>
        <textarea
          id="jfb-msg"
          className="jfb-textarea"
          rows={4}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder={
            type === "bug" ? "What happened, and what did you expect?" : "Share an idea or impression…"
          }
        />
      </div>
      <div className="jfb-field">
        <label className="jfb-label" htmlFor="jfb-contact">
          Email or phone <span className="jfb-opt">optional</span>
        </label>
        <input
          id="jfb-contact"
          className="jfb-input"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="you@example.com"
        />
      </div>
      {contact.trim() && (
        <label className="jfb-notify">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <span>Notify me of updates</span>
        </label>
      )}
      <div className="jfb-actions">
        <button type="button" className="btn btn-sm" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn btn-sm btn-primary" disabled={!msg.trim()}>
          Send feedback
        </button>
      </div>
    </form>
  );
}

/** Global feedback modal — mount once (in Base.tsx). Mirrors AskDock. */
export function FeedbackDialog() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"general" | "bug">("general");
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const k = (e as CustomEvent).detail?.kind;
      setKind(k === "bug" ? "bug" : "general");
      setOpen(true);
      void markFeatureTried("feedback");
    };
    window.addEventListener("open-feedback", onOpen);
    return () => window.removeEventListener("open-feedback", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fb-overlay print:hidden"
      style={picking ? { visibility: "hidden" } : undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="fb-modal">
        <div className="fb-modal-head">
          <div>
            <div className="fb-modal-title">Send feedback</div>
            <div className="fb-modal-sub mono">Ideas and bug reports both welcome</div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>
        <FeedbackForm key={kind} initialType={kind} onDone={() => setOpen(false)} onPickingChange={setPicking} />
        <div className="fb-modal-note">
          {feedbackBackendLive ? "Sent to Srinath." : "Stored locally in this demo. Nothing leaves your browser."}
        </div>
      </div>
    </div>
  );
}
